import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { hasConsent, onConsentReady } from 'utils/attribution/consent';
import { captureGclid, promoteSessionGclidToCookie } from 'utils/attribution/gclid';
import { rememberLanding } from 'utils/attribution/landing';
import { captureUtmByRegion, promoteSessionUtmToCookie } from 'utils/attribution/utm';
import { isEUVisitor } from 'utils/geo/region';

type RequestIdleCb = (cb: () => void, opts?: { timeout?: number }) => number;
type CancelIdleCb = (handle: number) => void;

const scheduleIdle = (cb: () => void, timeout = 2000): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const ric = (window as unknown as { requestIdleCallback?: RequestIdleCb }).requestIdleCallback;
  const cic = (window as unknown as { cancelIdleCallback?: CancelIdleCb }).cancelIdleCallback;

  if (ric && cic) {
    const handle = ric(cb, { timeout });

    return () => cic(handle);
  }

  const handle = window.setTimeout(cb, 0);

  return () => window.clearTimeout(handle);
};

const AttributionManager = () => {
  const router = useRouter();

  useEffect(() => {
    let stopConsent: (() => void) | undefined;

    // marketingAndAnalytics, not 'any'. UTM parameters and a gclid are
    // marketing and advertising data, never "functional", so consenting to
    // the functional category alone must not persist them to a 90-day
    // cookie. Without this consent they still land in sessionStorage and are
    // promoted later if the visitor does consent.
    //
    // This also makes attribution consistent with the rest of the tracking
    // stack: LazySegmentScript, LazyRudderStackScript and analytics/client
    // all gate EU visitors on marketingAndAnalytics too. Before this, a
    // functional-only EU visitor loaded neither Segment nor RudderStack yet
    // still had their attribution persisted for 90 days.
    //
    // The capture itself runs synchronously rather than on an idle callback:
    // it is one URL parse and one cookie write, and deferring it left a window
    // in which a form could load, or the visitor could leave for another
    // origin, before the campaign had been persisted at all.
    const capture = () => {
      rememberLanding();

      const isEU = isEUVisitor();
      const consent = hasConsent('marketingAndAnalytics');

      captureUtmByRegion({ isEU, hasConsent: consent, cookieDays: 90 });
      captureGclid({ isEU, hasConsent: consent, cookieDays: 90 });
    };

    capture();

    // Every internal navigation on the site is client-side, so a tagged
    // internal link (a CTA carrying its own utm_*) never went through the
    // capture above, while the same URL opened cold did. Both capture calls
    // no-op when the URL carries no campaign, so an untagged click leaves the
    // stored campaign exactly as it was.
    router.events.on('routeChangeComplete', capture);

    const cancelIdle = scheduleIdle(() => {
      stopConsent = onConsentReady(() => {
        promoteSessionUtmToCookie(90);
        promoteSessionGclidToCookie(90);
      });
    });

    return () => {
      router.events.off('routeChangeComplete', capture);
      cancelIdle();
      stopConsent?.();
    };
  }, []);

  return null;
};

export default AttributionManager;
